import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import type { PlutoOutreachDraft } from "../components/pluto/outreach-draft";
import { plutoToolSteps } from "../components/pluto/tool-status";
import { api } from "../settings";
import { getValidAccessToken } from "../services/token-manager";

export type PlutoReplyContext = {
  id: string;
  label: string;
  value: string;
};

export type PlutoReplyRequest = {
  prompt: string;
  contexts: readonly PlutoReplyContext[];
  /** Omitted on the first turn; the server mints and returns the conversation. */
  conversationId?: string;
};

export type PlutoReplyResponse = {
  text: string;
  conversationId: string;
  conversationTitle: string;
  usedToolNames: readonly string[];
  /** Outreach the turn wrote, when the user asked for some. */
  draft?: PlutoOutreachDraft;
};

/** One newline-delimited line of a streamed turn. Mirrors PlutoReplyStreamEvent. */
type PlutoReplyStreamEvent =
  | { type: "tool"; name: string }
  | { type: "delta"; generation: number; text: string; reasoning: boolean }
  | { type: "done"; reply: PlutoReplyResponse }
  | { type: "error"; message: string };

/**
 * A turn that the server explained. Anything else is a transport failure, whose
 * message would only ever be noise to a reader.
 */
export class PlutoReplyError extends Error {}

/**
 * The user stopped the turn. Not a failure: whatever had been written by then is
 * a real, if unfinished, answer and is kept rather than reported as a fault.
 *
 * `reachedServer` says whether the question got far enough to be stored. A stop
 * during the token refresh, before the request is sent, leaves nothing behind on
 * either side, and marking such a turn on screen would put back the mismatch
 * that storing an empty stopped answer set out to remove.
 */
export class PlutoReplyStopped extends Error {
  constructor(
    readonly partialText: string,
    readonly reachedServer: boolean,
  ) {
    super("Pluto was stopped before it finished.");
  }
}

/** A whole turn, including the tools it runs, has to fit inside this. */
const REPLY_TIMEOUT_MS = 150_000;

const isStreamEvent = (value: unknown): value is PlutoReplyStreamEvent =>
  typeof value === "object" && value !== null && "type" in value;

/**
 * Splits the response into whole lines. A chunk boundary lands anywhere, so a
 * half-received line is held back until the rest of it arrives.
 */
async function* readStreamEvents(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffered += decoder.decode(value, { stream: true });
      const lines = buffered.split("\n");
      buffered = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.trim()) continue;
        const parsed: unknown = JSON.parse(line);
        if (isStreamEvent(parsed)) yield parsed;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export const usePlutoReply = () => {
  // Every tool the turn has reached, so the user can see the work rather than
  // only whichever step happens to be running at the moment they look.
  const [toolSteps, setToolSteps] = useState<readonly string[]>([]);
  const [streamingText, setStreamingText] = useState("");
  const [reasoningText, setReasoningText] = useState("");
  const abortController = useRef<AbortController | null>(null);
  const activeToolName = toolSteps[toolSteps.length - 1] ?? null;

  const setActiveToolName = useCallback((name: string | null) => {
    setToolSteps((steps) => (name === null ? [] : plutoToolSteps(steps, name)));
  }, []);

  const clearProgress = useCallback(() => {
    setToolSteps([]);
    setStreamingText("");
    setReasoningText("");
  }, []);

  const streamReply = useCallback(
    async (request: PlutoReplyRequest) => {
      // One turn writes here at a time. Leaving a conversation does not stop the
      // turn it left behind, so a turn that starts while another is still
      // running ends that one first: two readers filling the same text would
      // interleave two answers, and the older one would go on to clear the
      // controller the newer one's Stop reaches for.
      abortController.current?.abort();
      clearProgress();

      const controller = new AbortController();
      abortController.current = controller;

      try {
        return await readReply(request, controller, {
          setActiveToolName,
          setReasoningText,
          setStreamingText,
        });
      } finally {
        // A turn that has already been replaced leaves the newer one alone: the
        // progress on screen and the controller Stop reaches for are its now.
        if (abortController.current === controller) {
          abortController.current = null;
          clearProgress();
        }
      }
    },
    [clearProgress],
  );

  const mutation = useMutation({ mutationFn: streamReply });

  /** Stops the turn, and with it the work the server is still doing for it. */
  const stopReply = useCallback(() => {
    abortController.current?.abort();
  }, []);

  return { ...mutation, activeToolName, toolSteps, streamingText, reasoningText, stopReply };
};

/** Where a running turn reports what it has reached and what it is writing. */
type PlutoReplyProgress = {
  readonly setActiveToolName: (name: string | null) => void;
  readonly setStreamingText: (text: string) => void;
  readonly setReasoningText: (text: string) => void;
};

/** Runs one turn, reporting it as it arrives and returning the finished answer. */
const readReply = async (
  request: PlutoReplyRequest,
  controller: AbortController,
  progress: PlutoReplyProgress,
): Promise<PlutoReplyResponse> => {
  let response: Response;

  // Stopping is the user's decision at every point of a turn, including before
  // there is anything to stop: a turn cut off while it is still asking for a
  // token or waiting for response headers ends with nothing written rather than
  // with a fault to report.
  try {
    const token = await getValidAccessToken();
    // Joined by hand rather than by the shared Axios instance, which cannot read
    // a response as it arrives, so a configured trailing slash is trimmed here.
    response = await fetch(`${api.endpoint.replace(/\/$/, "")}/v3/pluto/replies/stream`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      signal: AbortSignal.any([AbortSignal.timeout(REPLY_TIMEOUT_MS), controller.signal]),
    });
  } catch (error) {
    if (controller.signal.aborted) throw new PlutoReplyStopped("", false);
    throw error;
  }

  if (!response.ok || response.body === null) {
    throw new Error(`Pluto reply stream failed with status ${String(response.status)}`);
  }

  let reply: PlutoReplyResponse | null = null;
  // A turn writes once per tool round and keeps only the last round, and a
  // retry starts the turn over, so text collected under a superseded
  // generation describes an answer that no longer exists.
  let generation: number | null = null;
  let answer = "";
  let reasoning = "";

  try {
    for await (const event of readStreamEvents(response.body)) {
      if (event.type === "tool") progress.setActiveToolName(event.name);
      else if (event.type === "error") throw new PlutoReplyError(event.message);
      else if (event.type === "delta") {
        if (event.generation !== generation) {
          generation = event.generation;
          answer = "";
          reasoning = "";
          progress.setStreamingText("");
          progress.setReasoningText("");
        }

        if (event.reasoning) {
          reasoning += event.text;
          progress.setReasoningText(reasoning);
        } else {
          answer += event.text;
          progress.setStreamingText(answer);
        }
      } else reply = event.reply;
    }
  } catch (error) {
    // Nothing that happens after the answer arrived can unmake it: the turn is
    // finished and stored by the time that line is sent, so a stop pressed in
    // the moment before the stream closes is too late to change what it says.
    if (reply !== null) return reply;
    // Stopping is the user's decision rather than a fault, so the unfinished
    // answer is handed back instead of being replaced by an error.
    if (controller.signal.aborted) throw new PlutoReplyStopped(answer, true);
    throw error;
  }

  // The answer is the last line of a turn that worked, so its absence means the
  // connection was cut part way through rather than that Pluto had nothing.
  if (reply === null) throw new Error("Pluto reply stream ended before the answer arrived");

  return reply;
};

export default usePlutoReply;
